import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Spinner, Button, Checkbox, Input, Select,
  CloseButton, Table, Thead, Tbody, Tr, Th, Td, Code, Progress,
} from "@chakra-ui/react";
import {
  applyTwoWaySync, startTwoWayBucketsPreview, applyTwoWayBuckets, PhiClientError,
  startTwoWaySyncPreview, twoWaySyncPreviewProgress,
} from "../../utils/rdsPhi";
import JsonFileDiff, { isJsonItem, JsonReviewList, canMerge } from "./JsonFileDiff";
import {
  refreshLiveDocument, isClinicNote, describeRefresh, yjsSiblingOf, collabServerReachable,
} from "../../utils/collabRefresh";

/**
 * Two-way sync between RDS and Supabase (RDS two_way_sync.py).
 *
 * Opening the dialog asks RDS to compare both databases row by row; nothing
 * is written until the operator has reviewed the plan, resolved any
 * conflicts, typed the phrase and pressed Apply. Each row travels the way
 * RDS proposes (the side that changed since the last sync wins, else the
 * newer update time); a conflict -- the same column changed on both sides,
 * or a row deleted on one side and edited on the other -- is never applied
 * until a side is picked for it -- or "Merge latest", which keeps the most
 * recently updated side. RDS re-compares on apply and refuses any
 * row that moved after this preview ("stale").
 *
 * Apply runs in small batches (APPLY_BATCH per call) so the operator sees
 * it happen: a progress bar, what is being written right now, and a live
 * log of every row/file with where it went and how it ended. "Stop" ends
 * the run after the batch in flight; nothing already written is undone.
 *
 * Rendered INLINE in the RDS panel, not as a Chakra Modal: the panel already
 * lives inside the HIPAA Compliance modal, and a Modal opened from inside
 * another Modal's body blanks both in the browser (see the note in
 * HipaaComplianceModal.js; jsdom does not reproduce it).
 */

const ACTIONS = {
  update_rds: { label: "Supabase → RDS", scheme: "blue", group: "to_rds" },
  insert_rds: { label: "New row → RDS", scheme: "blue", group: "to_rds" },
  update_supabase: { label: "RDS → Supabase", scheme: "purple", group: "to_supabase" },
  insert_supabase: { label: "New row → Supabase", scheme: "purple", group: "to_supabase" },
  merge: { label: "Merge both ways", scheme: "teal", group: "merge" },
  delete_rds: { label: "Delete in RDS", scheme: "red", group: "delete" },
  delete_supabase: { label: "Delete in Supabase", scheme: "red", group: "delete" },
  conflict: { label: "Conflict", scheme: "orange", group: "conflict" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "conflict", label: "Conflicts" },
  { key: "to_rds", label: "→ RDS" },
  { key: "to_supabase", label: "→ Supabase" },
  { key: "merge", label: "Merges" },
  { key: "delete", label: "Deletes" },
];

const PAGE = 200;

// Rows/files per apply call. Files move whole (up to 200 MB each), so
// their batches are small; each call re-reads both sides of what it writes.
const APPLY_BATCH = { tables: 50, buckets: 5 };

// How a finished row/file is shown in the live log.
const STATUS = {
  applied: { icon: "✓", color: "green.600", label: "synced" },
  in_sync: { icon: "=", color: "gray.500", label: "already in sync" },
  skipped: { icon: "–", color: "gray.500", label: "skipped" },
  stale: { icon: "⚠", color: "orange.600", label: "changed since the preview" },
  failed: { icon: "✕", color: "red.600", label: "failed" },
};

export const itemKey = (it) => `${it.schema}|${it.table}|${it.id}`;

// What the operator is told when RDS refuses, PHI-free.
function errorMessage(err) {
  if (err instanceof PhiClientError) {
    if (err.statusCode === 403) return "RDS refused: two-way sync needs the superadmin or doctor role in RDS.";
    if (err.statusCode === 409) return "A mirror sync is running right now — try again in a minute.";
    return `RDS returned ${err.statusCode}${typeof err.detail === "string" && err.detail ? `: ${err.detail}` : ""}.`;
  }
  return err?.message || "RDS could not be reached.";
}

function formatTs(iso) {
  if (!iso) return "no update time";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function show(value) {
  if (value === null || value === undefined) return <Text as="span" color="gray.400">empty</Text>;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

// The default decision for each row: everything RDS can place on its own is
// selected; a conflict waits for a side.
function initialDecisions(items) {
  const out = {};
  (items || []).forEach((it) => {
    out[itemKey(it)] = it.action === "conflict"
      ? { selected: false, resolution: "" }
      : { selected: it.preselect !== false, resolution: "auto" };
  });
  return out;
}

// Rows only one side holds (inserts, deletes, delete-vs-edit conflicts) move
// whole: the winning side's state -- the row, or its absence -- is copied.
// Same rules as RDS's two_way_sync.plan_for.
const AUTO_WINNER = { insert_rds: "supabase", delete_rds: "supabase", insert_supabase: "rds", delete_supabase: "rds" };

// What happens to one field once the row's decision is applied:
//   "into_rds"        RDS takes Supabase's value
//   "into_supabase"   Supabase takes RDS's value
//   "delete_rds" / "delete_supabase"   the whole row is deleted there
//   "pick"            a conflict with no side chosen yet
//   "skip"            the operator skipped the row
//   ""                this field already matches
function wholeRowTarget(it, resolution) {
  const winner = resolution === "rds" || resolution === "supabase" ? resolution : AUTO_WINNER[it.action];
  if (!winner) return "pick";
  const winnerHasRow = winner === "rds" ? it.fingerprint?.rds != null : it.fingerprint?.supabase != null;
  if (winner === "rds") return winnerHasRow ? "into_supabase" : "delete_supabase";
  return winnerHasRow ? "into_rds" : "delete_rds";
}

// "Merge latest" on a conflict is the side RDS named in item.latest
// (newer update time; Supabase when there is none; the edited row over a
// delete) -- the same rule two_way_sync.latest_choice applies on apply.
function effectiveResolution(it, resolution) {
  if (resolution !== "latest") return resolution;
  return it.action === "conflict" ? (it.latest?.side || "supabase") : "auto";
}

export function columnTarget(it, column, chosen) {
  if (chosen === "skip") return "skip";
  const resolution = effectiveResolution(it, chosen);
  const wholeRow = it.action.startsWith("insert_") || it.action.startsWith("delete_")
    || (it.action === "conflict" && it.conflict_kind !== "both_changed");
  if (wholeRow) return wholeRowTarget(it, resolution);
  const winnerTarget = (side) => (side === "rds" ? "into_supabase" : "into_rds");
  if (resolution === "rds" || resolution === "supabase") {
    if (it.conflict_columns.includes(column) || it.action !== "conflict") return winnerTarget(resolution);
  }
  if (it.columns_to_rds.includes(column)) return "into_rds";
  if (it.columns_to_supabase.includes(column)) return "into_supabase";
  if (it.conflict_columns.includes(column)) return "pick";
  return "";
}

const plain = (v) => (v === null || v === undefined || v === "" ? "empty"
  : typeof v === "string" ? v : JSON.stringify(v));

// The value both databases hold for one field after the sync.
function afterValue(values, target) {
  if (target === "into_rds") return values.supabase;
  if (target === "into_supabase") return values.rds;
  return undefined;
}

// Per database, exactly what will be written there.
function changeSentences(item, columns, resolution) {
  const values = item.values || {};
  const targets = columns.map((c) => columnTarget(item, c, resolution));
  if (targets.includes("skip")) return { skip: true };
  if (targets.includes("pick")) return { pick: true };
  const out = { rds: { deleted: false, added: false, parts: [] }, supabase: { deleted: false, added: false, parts: [] } };
  if (targets.includes("delete_rds")) out.rds.deleted = true;
  if (targets.includes("delete_supabase")) out.supabase.deleted = true;
  columns.forEach((c, i) => {
    const t = targets[i];
    if (t !== "into_rds" && t !== "into_supabase") return;
    const side = t === "into_rds" ? "rds" : "supabase";
    const isNew = item.fingerprint?.[side] == null;
    const to = afterValue(values[c] || {}, t);
    const other = side === "rds" ? "Supabase's" : "RDS's";
    out[side].added = isNew;
    if (item.kind === "object") {
      out[side].parts.push(isNew ? `${other} file is copied in (${plain(to)})`
        : `its file (${plain(values[c]?.[side])}) is replaced with ${other} version (${plain(to)})`);
    } else {
      out[side].parts.push(isNew ? `${c} = ${plain(to)}` : `${c} changes from ${plain(values[c]?.[side])} to ${plain(to)}`);
    }
  });
  return out;
}

function ChangeSummary({ item, columns, resolution }) {
  const c = changeSentences(item, columns, resolution);
  if (c.skip) return <Text fontSize="xs" mb={2} data-testid="two-way-change">Skipped: neither database changes.</Text>;
  if (c.pick) {
    return (
      <Text fontSize="xs" mb={2} color="orange.700" data-testid="two-way-change">
        Nothing will be written until you choose Keep RDS, Keep Supabase or Merge latest.
      </Text>
    );
  }
  const isObject = item.kind === "object";
  const line = (name, side) => {
    if (side.deleted) return <Text><b>{name}:</b> the {isObject ? "file" : "whole row"} is deleted.</Text>;
    if (!side.parts.length) {
      return <Text><b>{name}:</b> nothing changes (it already has {isObject ? "this file" : "these values"}).</Text>;
    }
    if (side.added && !isObject) return <Text><b>{name}:</b> a new row is added with {side.parts.join(", ")}.</Text>;
    return <Text><b>{name}:</b> {side.parts.join("; ")}.</Text>;
  };
  return (
    <Box fontSize="xs" mb={2} data-testid="two-way-change">
      <Text fontWeight="bold" mb={1}>What this sync will write:</Text>
      {line("RDS", c.rds)}
      {line("Supabase", c.supabase)}
    </Box>
  );
}

const TARGET_LABEL = {
  into_rds: "RDS takes Supabase's value",
  into_supabase: "Supabase takes RDS's value",
  delete_rds: "row deleted in RDS",
  delete_supabase: "row deleted in Supabase",
  pick: "choose a side first",
  skip: "skipped",
  "": "already the same",
};

const FILE_TARGET_LABEL = {
  ...TARGET_LABEL,
  into_rds: "RDS gets Supabase's file",
  into_supabase: "Supabase gets RDS's file",
  delete_rds: "file deleted in RDS",
  delete_supabase: "file deleted in Supabase",
};

function ValuesTable({ item, resolution }) {
  const values = item.values || {};
  const columns = Object.keys(values);
  if (!columns.length) {
    return (
      <Text fontSize="xs" color="gray.500">
        Columns: {[...item.columns_to_rds, ...item.columns_to_supabase, ...item.conflict_columns].join(", ") || "—"}
        {" "}(RDS sent no values — restart the RDS service if this persists)
      </Text>
    );
  }
  const isObject = item.kind === "object";
  const labels = isObject ? FILE_TARGET_LABEL : TARGET_LABEL;
  const noRow = <Text as="span" color="gray.400">{isObject ? "no file" : "no row"}</Text>;
  return (
    <Box>
      <ChangeSummary item={item} columns={columns} resolution={resolution} />
      <Table size="sm" variant="simple" data-testid="two-way-values">
        <Thead>
          <Tr><Th>{isObject ? "Item" : "Field"}</Th><Th>RDS now</Th><Th>Supabase now</Th><Th>Both after sync</Th><Th>What happens</Th></Tr>
        </Thead>
        <Tbody>
          {columns.map((c) => {
            const target = columnTarget(item, c, resolution);
            const moved = target === "into_rds" || target === "into_supabase";
            return (
              <Tr key={c} bg={item.conflict_columns.includes(c) ? "orange.50" : undefined}>
                <Td><Code fontSize="xs">{c}</Code></Td>
                <Td fontSize="xs" maxW="260px" whiteSpace="pre-wrap" wordBreak="break-word">
                  {item.fingerprint?.rds == null ? noRow : show(values[c].rds)}
                </Td>
                <Td fontSize="xs" maxW="260px" whiteSpace="pre-wrap" wordBreak="break-word">
                  {item.fingerprint?.supabase == null ? noRow : show(values[c].supabase)}
                </Td>
                <Td fontSize="xs" maxW="260px" whiteSpace="pre-wrap" wordBreak="break-word" fontWeight="bold">
                  {moved ? show(afterValue(values[c], target)) : target.startsWith("delete_") ? (isObject ? "file deleted" : "row deleted") : "—"}
                </Td>
                <Td fontSize="xs" whiteSpace="nowrap">{labels[target] ?? target}</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {isJsonItem(item) && (
        <JsonFileDiff item={item} target={columnTarget(item, columns[0], resolution)} resolution={resolution} />
      )}
    </Box>
  );
}

function ItemRow({ item, decision, expanded, onToggle, onResolve, onExpand }) {
  const meta = ACTIONS[item.action] || { label: item.action, scheme: "gray" };
  const isConflict = item.action === "conflict";
  const columns = [...item.columns_to_rds, ...item.columns_to_supabase, ...item.conflict_columns];
  return (
    <>
      <Tr data-testid="two-way-item" bg={isConflict && !decision.resolution ? "orange.50" : undefined}>
        <Td px={2}>
          <Checkbox isChecked={decision.selected} isDisabled={isConflict && !decision.resolution}
                    onChange={(e) => onToggle(e.target.checked)}
                    aria-label={`include ${item.schema}.${item.table} ${item.id}`} />
        </Td>
        <Td fontSize="xs" whiteSpace="nowrap">{item.kind === "object" ? `bucket ${item.table}` : `${item.schema}.${item.table}`}</Td>
        <Td fontSize="xs" title={item.id} maxW={item.kind === "object" ? "260px" : undefined} wordBreak="break-all">
          <Code fontSize="xs">{item.kind === "object" ? String(item.id) : String(item.id).slice(0, 8)}</Code>
        </Td>
        <Td><Badge colorScheme={meta.scheme} fontSize="10px">{meta.label}</Badge></Td>
        <Td fontSize="xs">
          <Text>{item.reason}</Text>
          {columns.length > 0 && <Text color="gray.500">{columns.join(", ")}</Text>}
        </Td>
        <Td fontSize="xs" whiteSpace="nowrap">
          <Text>RDS: {formatTs(item.rds_ts)}</Text>
          <Text>Supabase: {formatTs(item.source_ts)}</Text>
        </Td>
        <Td>
          <Select size="xs" value={decision.resolution} onChange={(e) => onResolve(e.target.value)}
                  data-testid="two-way-resolution" minW="130px">
            {isConflict ? <option value="">Choose…</option> : <option value="auto">As proposed</option>}
            {isConflict && <option value="latest">Merge latest</option>}
            <option value="rds">Keep RDS</option>
            <option value="supabase">Keep Supabase</option>
            <option value="skip">Skip</option>
          </Select>
          {isConflict && decision.resolution === "latest" && item.latest && (
            <Text fontSize="10px" color="gray.600" mt={1} maxW="180px" data-testid="two-way-latest-why">
              Keeps {item.latest.side === "rds" ? "RDS's" : "Supabase's"} values: {item.latest.why}
            </Text>
          )}
        </Td>
        <Td px={2}>
          <Button size="xs" variant="ghost" onClick={onExpand}>{expanded ? "Hide" : "Details"}</Button>
        </Td>
      </Tr>
      {expanded && (
        <Tr>
          <Td colSpan={8} bg="gray.50">
            <ValuesTable item={item} resolution={decision.resolution} />
          </Td>
        </Tr>
      )}
    </>
  );
}

function Summary({ totals }) {
  const t = totals || {};
  const chip = (label, n, scheme) => (
    <Badge key={label} colorScheme={n ? scheme : "gray"} fontSize="xs" px={2} py={0.5}>
      {label}: {n || 0}
    </Badge>
  );
  return (
    <HStack spacing={2} flexWrap="wrap" data-testid="two-way-summary">
      {chip("Supabase → RDS", (t.update_rds || 0) + (t.insert_rds || 0), "blue")}
      {chip("RDS → Supabase", (t.update_supabase || 0) + (t.insert_supabase || 0), "purple")}
      {chip("Merges", t.merge, "teal")}
      {chip("Deletes", (t.delete_rds || 0) + (t.delete_supabase || 0), "red")}
      {chip("Conflicts", t.conflict, "orange")}
      {chip("Identical", t.identical, "green")}
    </HStack>
  );
}

// What this scan actually cost. RDS remembers the update stamp each side
// showed last time (sync_scan.py), so a preview reads only the rows that
// moved rather than the whole table; this line is how an operator sees
// that, instead of wondering why the second press was instant. Counts, not
// a percentage: a scan that read 42 of 41,800 rows is 99.9% skipped, and
// rounding that to "100% unchanged" reads as "nothing happened".
// How often the panel asks RDS how far along the scan is. It starts quick --
// the operator has just pressed the button and a bar that moves at once
// reads as working -- then backs off, so a scan that takes minutes does not
// turn into thousands of requests.
const SCAN_POLL_FIRST_MS = 250;
const SCAN_POLL_MAX_MS = 1000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll a background scan until it answers, reporting each step.
 *
 * A 404 means RDS has forgotten the job (it only keeps a finished one for a
 * while), which the panel answers by saying so and asking for a new one
 * rather than sitting on a scan nobody is watching. A poll that fails for any
 * other reason does NOT fail the scan -- RDS is still working on it -- so it
 * is retried; giving up on a transient 503 would leave the operator staring
 * at an error for a scan that is about to finish.
 */
async function pollUntilDone(jobId, isCancelled, onStep) {
  if (!jobId) throw new Error("RDS did not return a scan job");
  let wait = SCAN_POLL_FIRST_MS;
  for (;;) {
    if (isCancelled?.()) return null;
    let state;
    try {
      state = await twoWaySyncPreviewProgress(jobId);
    } catch (err) {
      const status = err?.statusCode ?? err?.status;
      if (status === 404) {
        throw new Error("RDS no longer has this scan; press Compare again to start a new one.");
      }
      await sleep(wait);
      continue;
    }
    onStep?.(state);
    if (isCancelled?.()) return null;
    if (state?.state === "done") return state.result;
    if (state?.state === "error") throw new Error(state.error || "The scan failed");
    await sleep(wait);
    wait = Math.min(SCAN_POLL_MAX_MS, Math.round(wait * 1.6));
  }
}

// What a background scan is doing right now. RDS reads a first scan one page
// at a time from both sides -- for files, one FOLDER of one bucket at a time,
// which is the same story with more requests -- so it is minutes of waiting
// with no way to tell a working scan from a wedged one, and the scan publishes
// what it has done so this can show it.
//
// The percentage is real: RDS fixes the number of rows to get through before
// the scan starts, so the bar only moves forward. It is deliberately not a
// stopwatch -- a table's Supabase side arrives page by page while its RDS
// side arrives in one query, so a table jumps when it completes.
//
// `phase` is RDS's own words for the parts no row count can describe (which
// buckets it is listing, whether it is now probing which files Supabase can
// serve). When there is one it replaces the "Reading ..." line: after the
// listing the bar is at 100% while the scan is still probing, and a bar that
// is finished with nothing said about it reads as a finished scan.
function ScanProgress({ job, mode }) {
  const pct = typeof job?.percent === "number" ? job.percent : null;
  const read = job?.rows_read || 0;
  const expected = job?.rows_expected || null;
  const eta = typeof job?.eta_sec === "number" ? job.eta_sec : null;
  const secs = Math.round(job?.elapsed_sec || 0);
  const files = mode === "buckets";
  const scanning = (job?.tables || []).filter((t) => t.state === "scanning");

  return (
    <Box data-testid="two-way-scan-progress">
      <HStack spacing={2} align="center">
        <Spinner size="sm" />
        <Text fontSize="sm">
          {files
            ? "Listing every file on both sides…"
            : "Comparing both databases…"}
        </Text>
        {job?.tables_total > 0 && (
          <Text fontSize="xs" color="gray.600" data-testid="two-way-scan-tables">
            {job.tables_done || 0} of {job.tables_total} {files ? "buckets" : "tables"} done
          </Text>
        )}
      </HStack>

      {pct !== null && (
        <Progress
          value={Math.round(pct * 100)}
          size="sm"
          borderRadius="sm"
          mt={2}
          isAnimated
          hasStripe
          colorScheme="blue"
          data-testid="two-way-scan-bar"
          aria-label={`${Math.round(pct * 100)}% of the scan done`}
        />
      )}

      <Text fontSize="xs" color="gray.600" mt={1} data-testid="two-way-scan-detail">
        {expected
          ? `${read.toLocaleString()} of ${expected.toLocaleString()} ${files ? "files" : "rows"} read`
          : `${read.toLocaleString()} ${files ? "files" : "rows"} read`}
        {` · ${secs}s elapsed`}
        {eta !== null && eta > 0 ? ` · about ${Math.max(1, Math.round(eta))}s left` : ""}
      </Text>

      {job?.phase && (
        <Text fontSize="xs" color="gray.600" data-testid="two-way-scan-phase">{job.phase}</Text>
      )}
      {!job?.phase && scanning.length > 0 && (
        <Text fontSize="xs" color="gray.600" data-testid="two-way-scan-current">
          Reading {scanning.map((t) => t.table.replace(/^.*\./, "")).join(", ")}
        </Text>
      )}
    </Box>
  );
}

function ScanCost({ scan }) {
  if (!scan) return null;
  const read = scan.rows_read || 0;
  const same = scan.rows_unchanged || 0;
  const total = read + same;
  if (!total) return null;
  return (
    <Text fontSize="xs" color="gray.600" data-testid="two-way-scan-cost">
      Read {read.toLocaleString()} of {total.toLocaleString()} rows this scan
      {same > 0 && ` — ${same.toLocaleString()} were unchanged since the last one and were not read at all`}.
      {scan.incremental > 0 &&
        ` ${scan.incremental} table(s) read only their changed rows.`}
      {scan.full > 0 &&
        ` ${scan.full} table(s) had to be read whole (first scan, no update stamp to compare, or the daily full refresh).`}
    </Text>
  );
}

// Where one item goes under its decision, in words ("Supabase → RDS",
// "Conflict: keep RDS", ...); files say "file" where rows say "row".
function directionOf(it, resolution) {
  let label = ACTIONS[it.action]?.label || it.action;
  if (resolution === "merge") return "Merged both ways";
  if (it.action === "conflict") {
    const side = resolution === "latest" ? (it.latest?.side || "supabase") : resolution;
    label = side === "rds" ? "Conflict: keep RDS" : side === "supabase" ? "Conflict: keep Supabase" : "Conflict";
  }
  return it.kind === "object" ? label.replace("row", "file") : label;
}

function itemName(it) {
  return it.kind === "object" ? `${it.table}/${it.id}` : `${it.schema}.${it.table} ${String(it.id).slice(0, 8)}`;
}

// A clinic note was written by the sync: the collab server
// (backend/api/collab/views.py) shows it to whoever has the chart open --
// here and on the other deployments it knows (COLLAB_PEER_SERVERS) -- and
// records a "Version N Migrated" in the note's history, in both stores.
// Until this, an open editor kept the old note and saved it back over the
// merge. The merged document goes along when the panel built it; a file
// copied whole is read back by the server. Everything lands on the log
// entry; a failure never undoes the sync (it is reported instead).
async function afterNoteWritten(it, resolution, entry, mergedDoc) {
  const path = String(it.id);
  const document = resolution === "merge" ? mergedDoc : undefined;
  try {
    const res = await refreshLiveDocument({ path, bucket: it.table, document });
    entry.refresh = describeRefresh(res);
    entry.refreshLive = res?.room === "live" || (res?.peers || []).some((p) => p.ok);
    if (res?.version?.name) entry.version = res.version.name;
    else entry.versionError = res?.error || "not saved";
    if (res?.error) entry.refreshWarning = res.error;
  } catch (err) {
    entry.refreshError = errorMessage(err);
    entry.versionError = "the collab server was not reached";
  }
  return entry;
}

// Sum the per-batch apply answers into one result.
function mergeResults(acc, res) {
  const counts = { ...acc.counts };
  Object.entries(res?.counts || {}).forEach(([k, v]) => { counts[k] = (counts[k] || 0) + (Number(v) || 0); });
  return { ...acc, ok: acc.ok && !!res?.ok, counts, results: acc.results.concat(res?.results || []) };
}

function SyncLog({ entries }) {
  if (!entries?.length) return null;
  return (
    <Box maxH="240px" overflowY="auto" borderWidth="1px" borderRadius="md" px={2} py={1} data-testid="two-way-log">
      {entries.slice().reverse().map((e) => {
        const st = STATUS[e.status] || { icon: "•", color: "gray.500", label: e.status };
        return (
          <HStack key={e.key} spacing={2} fontSize="xs" py="1px" data-testid="two-way-log-entry">
            <Text as="span" color={st.color} fontWeight="bold" w="12px" textAlign="center">{st.icon}</Text>
            <Badge colorScheme={ACTIONS[e.action]?.scheme || "gray"} fontSize="9px">{e.direction}</Badge>
            <Code fontSize="xs" noOfLines={1} title={e.name}>{e.name}</Code>
            <Text as="span" color={st.color}>{st.label}</Text>
            {e.error && <Text as="span" color="gray.600" noOfLines={1} title={e.error}>— {e.error}</Text>}
            {e.refresh && <Text as="span" color="green.700" data-testid="two-way-log-refresh">· {e.refresh}</Text>}
            {e.refreshError && (
              <Text as="span" color="orange.600" noOfLines={1} title={e.refreshError} data-testid="two-way-log-refresh-error">
                · open editors NOT refreshed: {e.refreshError}
              </Text>
            )}
            {e.refreshWarning && <Text as="span" color="orange.600" noOfLines={1} title={e.refreshWarning}>· {e.refreshWarning}</Text>}
            {e.version && <Badge colorScheme="teal" fontSize="9px" data-testid="two-way-log-version">{e.version}</Badge>}
            {e.versionError && (
              <Text as="span" color="orange.600" noOfLines={1} title={e.versionError} data-testid="two-way-log-version-error">
                · version not saved: {e.versionError}
              </Text>
            )}
          </HStack>
        );
      })}
    </Box>
  );
}

function LiveProgress({ progress, mode }) {
  const { total, done, current, log, counts, stopping } = progress;
  const pct = total ? Math.round((done * 100) / total) : 0;
  const noun = mode === "buckets" ? "files" : "rows";
  return (
    <VStack align="stretch" spacing={2} data-testid="two-way-progress">
      <HStack justify="space-between">
        <Text fontWeight="semibold">
          {stopping ? "Stopping after this batch… " : "Syncing… "}
          {done} of {total} {noun}
        </Text>
        <Text fontSize="xs" color="gray.600" data-testid="two-way-progress-pct">{pct}%</Text>
      </HStack>
      <Progress value={pct} size="sm" colorScheme="green" hasStripe isAnimated borderRadius="md"
                aria-label="Sync progress" data-testid="two-way-progress-bar" />
      <HStack spacing={3} fontSize="xs" color="gray.700" flexWrap="wrap">
        <Text>✓ {counts.applied || 0} synced</Text>
        <Text>= {counts.in_sync || 0} already in sync</Text>
        <Text>– {counts.skipped || 0} skipped</Text>
        <Text color={counts.stale ? "orange.600" : undefined}>⚠ {counts.stale || 0} changed since the preview</Text>
        <Text color={counts.failed ? "red.600" : undefined}>✕ {counts.failed || 0} failed</Text>
      </HStack>
      {current.length > 0 && (
        <Box bg="blue.50" borderRadius="md" px={2} py={1} data-testid="two-way-current">
          <Text fontSize="xs" fontWeight="semibold" mb={1}>Writing now</Text>
          {current.map(({ it, resolution }) => (
            <HStack key={itemKey(it)} spacing={2} fontSize="xs">
              <Spinner size="xs" />
              <Badge colorScheme={ACTIONS[it.action]?.scheme || "gray"} fontSize="9px">{directionOf(it, resolution)}</Badge>
              <Code fontSize="xs" noOfLines={1} title={itemName(it)}>{itemName(it)}</Code>
            </HStack>
          ))}
        </Box>
      )}
      <SyncLog entries={log} />
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Simple view: the same plan as the table, grouped by folder (files) or by
// table (rows), one line per group saying how many go which way. Expanding a
// group lists its items by name with an arrow; conflicts keep their picker.
// ---------------------------------------------------------------------------

// The simple direction of one item, for its arrow and colour.
const SIMPLE_DIR = {
  to_supabase: { arrow: "RDS → Supabase", scheme: "purple" },
  to_rds: { arrow: "Supabase → RDS", scheme: "blue" },
  merge: { arrow: "Both ways", scheme: "teal" },
  delete: { arrow: "Delete", scheme: "red" },
  conflict: { arrow: "Conflict", scheme: "orange" },
};

const GROUP_PAGE = 50;

export function groupOf(it) {
  if (it.kind !== "object") return { key: `${it.schema}.${it.table}`, label: `${it.schema}.${it.table}` };
  const path = String(it.id);
  const cut = path.lastIndexOf("/");
  const folder = cut > 0 ? path.slice(0, cut) : "(top folder)";
  return { key: `${it.table}|${folder}`, label: `${it.table} / ${folder}` };
}

function shortName(it) {
  if (it.kind !== "object") return String(it.id);
  const path = String(it.id);
  return path.slice(path.lastIndexOf("/") + 1);
}

// Text the search box matches against: table/bucket, id/path, why.
export function matchesSearch(it, q) {
  if (!q) return true;
  return `${it.schema}.${it.table} ${it.table}/${it.id} ${it.reason || ""}`.toLowerCase().includes(q.toLowerCase());
}

function DirectionTiles({ items, filter, onFilter }) {
  const count = (g) => items.filter((it) => ACTIONS[it.action]?.group === g).length;
  const tiles = [
    { key: "to_supabase", title: "RDS → Supabase", hint: "newer in RDS", scheme: "purple" },
    { key: "to_rds", title: "Supabase → RDS", hint: "newer in Supabase", scheme: "blue" },
    { key: "conflict", title: "Conflicts", hint: "you choose a side", scheme: "orange" },
    { key: "merge", title: "Both ways", hint: "fields merged", scheme: "teal" },
    { key: "delete", title: "Deletes", hint: "removed on one side", scheme: "red" },
  ].map((t) => ({ ...t, n: count(t.key) })).filter((t) => t.n > 0 || t.key === "to_supabase" || t.key === "to_rds");
  return (
    <HStack spacing={2} flexWrap="wrap" data-testid="two-way-tiles">
      {tiles.map((t) => {
        const active = filter === t.key;
        return (
          <Box key={t.key} as="button" type="button" onClick={() => onFilter(active ? "all" : t.key)}
               borderWidth="2px" borderColor={active ? `${t.scheme}.400` : `${t.scheme}.100`}
               bg={t.n ? `${t.scheme}.50` : "gray.50"} borderRadius="md" px={3} py={2} minW="130px" textAlign="left"
               data-testid={`two-way-tile-${t.key}`}>
            <Text fontSize="xl" fontWeight="bold" color={t.n ? `${t.scheme}.700` : "gray.400"}>{t.n}</Text>
            <Text fontSize="xs" fontWeight="semibold">{t.title}</Text>
            <Text fontSize="10px" color="gray.500">{t.hint}</Text>
          </Box>
        );
      })}
    </HStack>
  );
}

function SimpleItem({ item, decision, expanded, onToggle, onResolve, onExpand }) {
  const group = ACTIONS[item.action]?.group;
  const dir = SIMPLE_DIR[group] || { arrow: item.action, scheme: "gray" };
  const isConflict = item.action === "conflict";
  return (
    <Box data-testid="two-way-simple-item">
      <HStack spacing={2} py={1} pl={6} fontSize="xs">
        <Checkbox size="sm" isChecked={decision.selected} isDisabled={isConflict && !decision.resolution}
                  onChange={(e) => onToggle(e.target.checked)} aria-label={`include ${item.table} ${item.id}`} />
        <Badge colorScheme={dir.scheme} fontSize="9px" minW="96px" textAlign="center">{dir.arrow}</Badge>
        <Code fontSize="xs" noOfLines={1} title={String(item.id)} maxW="320px">{shortName(item)}</Code>
        <Text color="gray.500" noOfLines={1} flex="1" title={item.reason}>{item.reason}</Text>
        {isConflict && (
          <Select size="xs" w="130px" value={decision.resolution} onChange={(e) => onResolve(e.target.value)}
                  data-testid="two-way-simple-resolution">
            <option value="">Choose…</option>
            <option value="latest">Merge latest</option>
            <option value="rds">Keep RDS</option>
            <option value="supabase">Keep Supabase</option>
            <option value="skip">Skip</option>
          </Select>
        )}
        <Button size="xs" variant="link" onClick={onExpand}>{expanded ? "hide" : "what changes?"}</Button>
      </HStack>
      {expanded && (
        <Box ml={6} mb={2} p={2} bg="gray.50" borderRadius="md">
          <ValuesTable item={item} resolution={decision.resolution} />
        </Box>
      )}
    </Box>
  );
}

function SimpleView({ items, decisions, expanded, onExpand, onToggle, onResolve, onToggleGroup }) {
  const [open, setOpen] = useState({});
  const [limit, setLimit] = useState(GROUP_PAGE);
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const g = groupOf(it);
      if (!map.has(g.key)) map.set(g.key, { ...g, items: [] });
      map.get(g.key).items.push(it);
    });
    // Conflicts first, then the biggest groups.
    return [...map.values()].sort((a, b) => {
      const ca = a.items.some((it) => it.action === "conflict");
      const cb = b.items.some((it) => it.action === "conflict");
      return ca === cb ? b.items.length - a.items.length : ca ? -1 : 1;
    });
  }, [items]);
  // With only a few groups (e.g. after a search) they open by themselves.
  const isOpen = (g) => open[g.key] ?? (groups.length <= 3);
  return (
    <VStack align="stretch" spacing={0} borderWidth="1px" borderRadius="md" data-testid="two-way-simple">
      {groups.slice(0, limit).map((g) => {
        const counts = {};
        g.items.forEach((it) => { const k = ACTIONS[it.action]?.group || "other"; counts[k] = (counts[k] || 0) + 1; });
        const pickable = g.items.filter((it) => it.action !== "conflict" || decisions[itemKey(it)]?.resolution);
        const on = pickable.filter((it) => decisions[itemKey(it)]?.selected).length;
        return (
          <Box key={g.key} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }} data-testid="two-way-group">
            <HStack spacing={2} px={2} py={2} cursor="pointer" _hover={{ bg: "gray.50" }}
                    onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen(g) }))}>
              <Checkbox size="sm" isChecked={pickable.length > 0 && on === pickable.length}
                        isIndeterminate={on > 0 && on < pickable.length} isDisabled={!pickable.length}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onToggleGroup(g.items, e.target.checked)}
                        aria-label={`include all of ${g.label}`} />
              <Text fontSize="xs" color="gray.500" w="10px">{isOpen(g) ? "▾" : "▸"}</Text>
              <Text fontSize="sm" fontWeight="semibold" noOfLines={1} flex="1" title={g.label}>{g.label}</Text>
              {Object.entries(SIMPLE_DIR).map(([k, d]) => counts[k] ? (
                <Badge key={k} colorScheme={d.scheme} fontSize="10px">{d.arrow}: {counts[k]}</Badge>
              ) : null)}
            </HStack>
            {isOpen(g) && (
              <Box pb={1}>
                {g.items.map((it) => {
                  const key = itemKey(it);
                  return (
                    <SimpleItem key={key} item={it} decision={decisions[key] || { selected: false, resolution: "" }}
                                expanded={!!expanded[key]} onExpand={() => onExpand(key)}
                                onToggle={(v) => onToggle(key, v)} onResolve={(r) => onResolve(it, r)} />
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
      {groups.length > limit && (
        <Button size="xs" variant="ghost" onClick={() => setLimit((l) => l + GROUP_PAGE)}>
          Show {Math.min(GROUP_PAGE, groups.length - limit)} more groups of {groups.length - limit}
        </Button>
      )}
    </VStack>
  );
}

// What happened to the clinic notes the sync wrote (see afterNoteWritten).
function NotesSummary({ entries }) {
  const notes = (entries || []).filter((e) => e.refresh || e.refreshError || e.version || e.versionError);
  if (!notes.length) return null;
  const live = notes.filter((e) => e.refreshLive).length;
  const versions = notes.filter((e) => e.version).length;
  const failed = notes.filter((e) => e.refreshError || e.versionError).length;
  return (
    <Alert status={failed ? "warning" : "info"} fontSize="xs" data-testid="two-way-notes-summary">
      <AlertIcon />
      {notes.length} clinic note{notes.length === 1 ? "" : "s"} written · {live} shown to editors who had {live === 1 ? "it" : "them"} open
      · {versions} "Version N Migrated" saved to history
      {failed > 0 && ` · ${failed} with a problem (see the log)`}
    </Alert>
  );
}

function ResultView({ result }) {
  const c = result?.counts || {};
  // `hidden`: a file Supabase lists but cannot serve, skipped on purpose.
  const problems = (result?.results || []).filter((r) => r.status !== "applied" && r.status !== "in_sync"
    && !r.hidden && !/cannot serve it/.test(r.error || ""));
  return (
    <VStack align="stretch" spacing={3} data-testid="two-way-result">
      <Alert status={result?.ok ? "success" : "warning"} fontSize="sm">
        <AlertIcon />
        {c.applied || 0} synced · {c.in_sync || 0} already in sync · {c.skipped || 0} skipped
        · {c.stale || 0} changed since the preview · {c.failed || 0} failed
      </Alert>
      {result?.stopped > 0 && (
        <Alert status="info" fontSize="xs" data-testid="two-way-stopped">
          <AlertIcon />
          Stopped: {result.stopped} selected change(s) were not applied. Compare again to see what is left.
        </Alert>
      )}
      <NotesSummary entries={result?.log} />
      <SyncLog entries={result?.log} />
      {problems.length > 0 && (
        <Table size="sm">
          <Thead><Tr><Th>Row</Th><Th>Status</Th><Th>Why</Th></Tr></Thead>
          <Tbody>
            {problems.slice(0, 200).map((r) => (
              <Tr key={`${r.schema}|${r.table}|${r.id}`}>
                <Td fontSize="xs">{r.schema}.{r.table} <Code fontSize="xs">{String(r.id).slice(0, 8)}</Code></Td>
                <Td fontSize="xs">{r.status}</Td>
                <Td fontSize="xs">{r.error || "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </VStack>
  );
}

// What the section compares: database rows, or the files in the buckets.
const MODES = [
  { key: "tables", label: "Tables (rows)" },
  { key: "buckets", label: "Files (buckets)" },
];
// The owner ruled (2026-10-12) that the panel offers Files only; the table
// sync stays here, reachable through the `modes` prop.
const DEFAULT_MODES = ["buckets"];

const VIEW_KEY = "two_way_sync_view";

function savedView(fallback) {
  try { return window.localStorage.getItem(VIEW_KEY) || fallback; } catch { return fallback; }
}

export default function TwoWaySyncDialog({ isOpen, onClose, onApplied, initialView, modes = DEFAULT_MODES }) {
  const shownModes = MODES.filter((m) => modes.includes(m.key));
  const [mode, setMode] = useState(shownModes[0]?.key || "buckets");
  const [view, setViewState] = useState(() => initialView || savedView("simple"));
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Whether this page can reach the collab server that refreshes open
  // editors and keeps the migrated version (asked when the dialog opens in
  // Files mode). null = not asked / fine; a string = the problem, shown
  // next to Apply so the operator can fix it BEFORE syncing.
  const [collabProblem, setCollabProblem] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [limit, setLimit] = useState(PAGE);
  const [confirm, setConfirm] = useState("");
  const [applying, setApplying] = useState(false);
  // Files only: Apply first shows every selected .json file's diff; the
  // write starts from that screen.
  const [reviewing, setReviewing] = useState(false);
  // itemKey -> the merged note the operator reviewed (resolution "merge").
  const [mergedDocs, setMergedDocs] = useState({});
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [scanJob, setScanJob] = useState(null);
  const stopRef = useRef(false);
  const scanPollRef = useRef({ cancel: false });

  // A first scan of a real table -- or of a real bucket -- takes minutes: it
  // reads every row of every mirrored table, or every folder of every mirrored
  // bucket, from both sides, and one blocking request says nothing until it is
  // over. Both go through RDS's background job instead, and the panel polls it
  // so the operator can see it working.
  const compare = useCallback(async (which = "tables") => {
    scanPollRef.current.cancel = true;
    setLoading(true);
    setError(null);
    setResult(null);
    setConfirm("");
    setReviewing(false);
    setMergedDocs({});
    setExpanded({});
    setLimit(PAGE);
    setScanJob(null);
    try {
      const started = which === "buckets"
        ? await startTwoWayBucketsPreview()
        : await startTwoWaySyncPreview();
      scanPollRef.current = { cancel: false, jobId: started?.job_id };
      setScanJob(started);
      const payload = await pollUntilDone(
        started?.job_id,
        () => scanPollRef.current.cancel,
        (step) => setScanJob((prev) => ({ ...prev, ...step })),
      );
      if (scanPollRef.current.cancel) return;
      setPreview(payload);
      setDecisions(initialDecisions(payload?.items));
    } catch (err) {
      setPreview(null);
      setError(errorMessage(err));
    } finally {
      setScanJob(null);
      setLoading(false);
    }
  }, []);

  // Compare on open; switching Tables/Files re-compares (and drops the
  // other mode's plan, which its apply could not use anyway).
  useEffect(() => {
    if (!isOpen || mode !== "buckets") { setCollabProblem(null); return undefined; }
    let gone = false;
    collabServerReachable().then((r) => {
      if (!gone) setCollabProblem(r.ok ? null : `${r.url}: ${r.reason}`);
    });
    return () => { gone = true; };
  }, [isOpen, mode]);

  useEffect(() => {
    if (isOpen) compare(mode);
    else {
      // stop following a scan nobody is looking at. RDS keeps working on it
      // either way -- it releases its one-preview lock when it finishes, so
      // closing the dialog does not wedge the next one.
      scanPollRef.current.cancel = true;
      setPreview(null); setResult(null); setError(null); setFilter("all"); setSearch("");
    }
    // Unmounting is a third way to stop: the parent can take the section
    // away entirely (the HIPAA modal closing, a route change) without ever
    // passing isOpen=false, and a poll loop that outlives the panel keeps
    // asking RDS about a scan nobody is watching.
    return () => { scanPollRef.current.cancel = true; };
  }, [isOpen, mode, compare]);

  // Files Supabase lists but cannot serve are hidden (owner rule,
  // 2026-10-28): the RDS service drops them already; an older one still
  // sends them, so they are filtered here too.
  const items = useMemo(() => (preview?.items || []).filter((it) => !it.unservable), [preview]);
  const visible = useMemo(
    () => items.filter((it) => (filter === "all" || ACTIONS[it.action]?.group === filter) && matchesSearch(it, search.trim())),
    [items, filter, search],
  );
  const setView = (v) => {
    setViewState(v);
    try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* per-viewer convenience only */ }
  };
  const selected = useMemo(
    () => items.filter((it) => decisions[itemKey(it)]?.selected && decisions[itemKey(it)]?.resolution !== "skip"),
    [items, decisions],
  );
  const unresolved = items.filter((it) => it.action === "conflict" && !decisions[itemKey(it)]?.resolution).length;
  const phrase = preview?.confirm_phrase || "SYNC";
  const jsonSelected = mode === "buckets" ? selected.filter(isJsonItem) : [];
  const needsReview = jsonSelected.length > 0 && !reviewing;
  const fileTarget = (it) => columnTarget(it, "file", decisions[itemKey(it)]?.resolution);
  const mergePending = jsonSelected.filter(
    (it) => decisions[itemKey(it)]?.resolution === "merge" && !mergedDocs[itemKey(it)]).length;
  // Said next to Apply, so a disabled button is never a mystery.
  const blockedReason = !preview || loading || applying || needsReview ? null
    : !selected.length ? "Select at least one change."
      : mergePending > 0
        ? `${mergePending} merged note${mergePending === 1 ? "" : "s"} not reviewed yet: open ${mergePending === 1 ? "it" : "them"} above (Next unreviewed note).`
        : confirm !== phrase ? `Type ${phrase} to enable Apply.` : null;

  // Notes both sides hold are merged by default; an explicit Keep RDS /
  // Keep Supabase stays as chosen.
  const startReview = () => {
    setDecisions((d) => {
      const next = { ...d };
      jsonSelected.forEach((it) => {
        const cur = next[itemKey(it)];
        if (canMerge(it) && ["auto", "latest", ""].includes(cur?.resolution || "")) {
          next[itemKey(it)] = { ...cur, resolution: "merge", selected: true };
        }
      });
      return next;
    });
    setReviewing(true);
  };
  const resolveReviewed = (it, resolution) => {
    setDecision(itemKey(it), { resolution, selected: true });
    if (resolution !== "merge") setMergedDocs((m) => { const n = { ...m }; delete n[itemKey(it)]; return n; });
  };
  const onMerged = useCallback((it, doc) => setMergedDocs((m) => ({ ...m, [itemKey(it)]: doc })), []);
  const onNotMergeable = useCallback((it) => {
    setDecisions((d) => (d[itemKey(it)]?.resolution === "merge"
      ? { ...d, [itemKey(it)]: { ...d[itemKey(it)], resolution: it.action === "conflict" ? "latest" : "auto" } }
      : d));
  }, []);

  const setDecision = (key, patch) => setDecisions((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const resolve = (it, resolution) => {
    const key = itemKey(it);
    if (!resolution) setDecision(key, { resolution: "", selected: false });
    else setDecision(key, { resolution, selected: resolution !== "skip" });
  };

  const resolveConflicts = (resolution, rows = visible) => {
    setDecisions((d) => {
      const next = { ...d };
      rows.filter((it) => it.action === "conflict").forEach((it) => {
        next[itemKey(it)] = { resolution, selected: true };
      });
      return next;
    });
  };

  // "Merge latest" for everything: each conflict takes the side
  // item.latest names, every other change goes as proposed (newest copy wins
  // where no baseline says otherwise). Items RDS left unselected on purpose
  // (preselect false) stay unselected.
  const mergeLatestAll = () => {
    setDecisions(() => {
      const next = {};
      items.forEach((it) => {
        next[itemKey(it)] = {
          resolution: "latest",
          selected: it.action === "conflict" || it.preselect !== false,
        };
      });
      return next;
    });
  };

  const selectVisible = (on, rows = visible) => {
    setDecisions((d) => {
      const next = { ...d };
      rows.forEach((it) => {
        const cur = next[itemKey(it)];
        if (it.action === "conflict" && !cur.resolution) return;
        next[itemKey(it)] = { ...cur, selected: on };
      });
      return next;
    });
  };

  // Batch by batch, so the progress bar, "Writing now" and the log move as
  // RDS answers. A refused call (role, confirm phrase, busy mirror) stops
  // the run; what earlier batches wrote stays written and is reported.
  const apply = async () => {
    // A merged note's `.yjs` sibling is deleted by the merge itself (RDS
    // two_way_storage._write_merged, on both sides), so a decision about it
    // would only come back "changed since the preview": it is left out and
    // said so in the log.
    const mergedNotes = new Set(selected
      .filter((it) => (decisions[itemKey(it)]?.resolution || "auto") === "merge")
      .map((it) => `${it.table}|${it.id}`));
    const log = [];
    const plan = [];
    selected.forEach((it) => {
      const sibling = yjsSiblingOf(String(it.id));
      if (sibling && mergedNotes.has(`${it.table}|${sibling}`)) {
        log.push({
          key: itemKey(it), name: itemName(it), action: it.action, direction: directionOf(it, "skip"),
          status: "skipped", error: "removed on both sides by the merge of its note",
        });
        return;
      }
      plan.push({ it, resolution: decisions[itemKey(it)].resolution || "auto" });
    });
    const size = APPLY_BATCH[mode] || 25;
    let merged = { ok: true, counts: {}, results: [] };
    let batches = 0;
    stopRef.current = false;
    setApplying(true);
    setReviewing(false);
    setError(null);
    setProgress({ total: plan.length, done: 0, current: [], log: log.slice(), counts: {}, stopping: false });
    try {
      for (let i = 0; i < plan.length; i += size) {
        if (stopRef.current) {
          merged = { ...merged, ok: false, stopped: plan.length - i };
          break;
        }
        const batch = plan.slice(i, i + size);
        setProgress((p) => ({ ...p, current: batch }));
        // eslint-disable-next-line no-await-in-loop -- one batch at a time is the point
        const res = mode === "buckets"
          ? await applyTwoWayBuckets(batch.map(({ it, resolution }) => ({
            bucket: it.table,
            path: String(it.id),
            resolution,
            action: it.action,
            fingerprint: it.fingerprint,
            ...(resolution === "merge" ? { merged: mergedDocs[itemKey(it)] } : {}),
          })), { confirm })
          : await applyTwoWaySync(batch.map(({ it, resolution }) => ({
            schema: it.schema,
            table: it.table,
            id: String(it.id),
            resolution,
            fingerprint: it.fingerprint,
          })), { confirm });
        batches += 1;
        merged = mergeResults(merged, res);
        const byKey = new Map((res?.results || []).map((r) => [`${r.schema}|${r.table}|${r.id}`, r]));
        const entries = batch.map(({ it, resolution }) => {
          const r = byKey.get(itemKey(it));
          const entry = {
            key: itemKey(it), name: itemName(it), action: it.action, direction: directionOf(it, resolution),
            status: r?.status || (res?.ok ? "applied" : "failed"), error: r?.error || null,
          };
          log.push(entry);
          return { it, resolution, entry };
        });
        // Clinic notes the sync just merged or replaced: push the new note
        // into any editor that has it open right now, and keep a
        // "Version N Migrated" in the note's history.
        // eslint-disable-next-line no-await-in-loop -- the batch's log waits for this
        await Promise.all(entries
          .filter(({ it, entry }) => mode === "buckets" && entry.status === "applied" && isClinicNote(it.table, it.id))
          .map(({ it, resolution, entry }) => afterNoteWritten(it, resolution, entry, mergedDocs[itemKey(it)])));
        const done = Math.min(i + size, plan.length);
        const snapshot = log.slice();
        const counts = { ...merged.counts };
        setProgress((p) => ({ ...p, done, current: [], log: snapshot, counts }));
      }
    } catch (err) {
      merged = { ...merged, ok: false };
      setError(errorMessage(err));
    } finally {
      setApplying(false);
      setProgress(null);
      if (batches > 0 || log.length) {
        const final = { ...merged, log };
        setResult(final);
        if (onApplied) onApplied(final);
      }
    }
  };

  const stop = () => {
    stopRef.current = true;
    setProgress((p) => (p ? { ...p, stopping: true } : p));
  };

  const skippedTables = (preview?.tables || []).filter((t) => t.skipped);

  // Bring the section into view when it opens: it appears below the card
  // the operator just clicked, possibly under the fold.
  const boxRef = useRef(null);
  useEffect(() => {
    if (isOpen && boxRef.current?.scrollIntoView) boxRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Box ref={boxRef} borderWidth="2px" borderColor="blue.200" borderRadius="lg" bg="white"
         data-testid="two-way-dialog">
      <HStack justify="space-between" px={4} py={3} borderBottomWidth="1px">
        <Text fontWeight="bold">Sync RDS ⇄ Supabase</Text>
        {!applying && <CloseButton size="sm" onClick={onClose} aria-label="Close sync review" />}
      </HStack>
      <Box px={4} py={3}>
          <VStack align="stretch" spacing={3} fontSize="sm">
            {shownModes.length > 1 && (
            <HStack spacing={1}>
              {shownModes.map((m) => (
                <Button key={m.key} size="xs" variant={mode === m.key ? "solid" : "outline"} colorScheme="blue"
                        isDisabled={loading || applying} onClick={() => setMode(m.key)}
                        data-testid={`two-way-mode-${m.key}`}>
                  {m.label}
                </Button>
              ))}
            </HStack>
            )}
            <Text fontSize="xs" color="gray.600">
              {mode === "buckets"
                ? "Files are compared by their content fingerprint; nothing is downloaded until you apply. "
                : ""}
              Both databases are compared {mode === "buckets" ? "file by file" : "row by row"}. A change made on one side only is copied to the other;
              when both sides changed the same field, or one side deleted a row the other edited, it is a
              conflict and you choose which side to keep. Nothing is written until you press Apply.
            </Text>

            {loading && scanJob && (
              <ScanProgress job={scanJob} mode={mode} />
            )}
            {loading && !scanJob && (
              <HStack>
                <Spinner size="sm" />
                <Text>{mode === "buckets" ? "Starting the file comparison…" : "Starting the comparison…"}</Text>
              </HStack>
            )}
            {error && <Alert status="error" fontSize="sm"><AlertIcon />{error}</Alert>}

            {applying && progress && <LiveProgress progress={progress} mode={mode} />}
            {reviewing && !applying && !result && (
              <JsonReviewList items={jsonSelected} targetOf={fileTarget}
                              otherCount={selected.length - jsonSelected.length}
                              resolutionOf={(it) => decisions[itemKey(it)]?.resolution || "auto"}
                              onResolve={resolveReviewed} merged={mergedDocs} keyOf={itemKey}
                              onMerged={onMerged} onNotMergeable={onNotMergeable} />
            )}

            {result && <ResultView result={result} />}

            {preview && !loading && !result && !applying && !reviewing && (
              <>
                {preview.mirror_warning && (
                  <Alert status="info" fontSize="xs"><AlertIcon />{preview.mirror_warning}</Alert>
                )}
                {view === "simple" ? (
                  <>
                    <DirectionTiles items={items} filter={filter} onFilter={(f) => { setFilter(f); setLimit(PAGE); }} />
                    <Text fontSize="xs" color="gray.600" data-testid="two-way-headline">
                      {(preview.totals?.identical || 0).toLocaleString()} {mode === "buckets" ? "files" : "rows"} are
                      already identical. {items.length} differ{items.length === 1 ? "s" : ""}; click a box to show only
                      that kind, or a group to see its {mode === "buckets" ? "files" : "rows"}.
                    </Text>
                  </>
                ) : <Summary totals={preview.totals} />}
                <ScanCost scan={preview.scan} />
                {skippedTables.length > 0 && (
                  <Alert status="warning" fontSize="xs" alignItems="flex-start">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Not compared:</Text>
                      {skippedTables.map((t) => (
                        <Text key={`${t.schema}.${t.table}`}>{t.schema}.{t.table} — {t.skipped}</Text>
                      ))}
                    </Box>
                  </Alert>
                )}
                {preview.truncated && (
                  <Alert status="warning" fontSize="xs"><AlertIcon />
                    Showing the first {items.length} of {preview.pending} differences. Apply these, then compare again for the rest.
                  </Alert>
                )}

                {items.length === 0 ? (
                  <Alert status="success" fontSize="sm"><AlertIcon />
                    RDS and Supabase hold the same {mode === "buckets" ? "files" : "data"} — nothing to sync.
                  </Alert>
                ) : (
                  <>
                    <HStack spacing={2}>
                      <Input size="sm" placeholder={mode === "buckets" ? "Find a file, folder or patient id…" : "Find a table or row id…"}
                             value={search} onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
                             data-testid="two-way-search" />
                      <HStack spacing={0}>
                        {[["simple", "Simple"], ["detailed", "Detailed"]].map(([k, label]) => (
                          <Button key={k} size="sm" variant={view === k ? "solid" : "outline"} colorScheme="gray"
                                  borderRadius={k === "simple" ? "md 0 0 md" : "0 md md 0"}
                                  onClick={() => setView(k)} data-testid={`two-way-view-${k}`}>
                            {label}
                          </Button>
                        ))}
                      </HStack>
                    </HStack>
                    {search.trim() && (
                      <Text fontSize="xs" color="gray.600">{visible.length} of {items.length} match "{search.trim()}"</Text>
                    )}
                    <HStack spacing={1} flexWrap="wrap">
                      {view === "detailed" && FILTERS.map((f) => {
                        const n = f.key === "all" ? items.length
                          : items.filter((it) => ACTIONS[it.action]?.group === f.key).length;
                        return (
                          <Button key={f.key} size="xs" variant={filter === f.key ? "solid" : "outline"}
                                  colorScheme={f.key === "conflict" && n ? "orange" : "gray"}
                                  onClick={() => { setFilter(f.key); setLimit(PAGE); }}
                                  data-testid={`two-way-filter-${f.key}`}>
                            {f.label} ({n})
                          </Button>
                        );
                      })}
                      <Box flex="1" />
                      <Button size="xs" variant="ghost" onClick={() => selectVisible(true)}>Select shown</Button>
                      <Button size="xs" variant="ghost" onClick={() => selectVisible(false)}>Clear shown</Button>
                      <Button size="xs" colorScheme="teal"
                              onClick={mergeLatestAll}
                              title="Select every change: one-sided changes go as proposed, every conflict keeps the most recently updated copy"
                              data-testid="two-way-merge-latest-all">
                        Merge latest (all)
                      </Button>
                      {items.some((it) => it.action === "conflict") && (
                        <Button size="xs" colorScheme="orange"
                                onClick={() => resolveConflicts("latest", items)}
                                title="Every conflict takes the most recently updated side; with no update time to compare, the side RDS names under each row; a delete vs an edit keeps the edited row"
                                data-testid="two-way-conflicts-latest">
                          Merge latest (all conflicts)
                        </Button>
                      )}
                      {visible.some((it) => it.action === "conflict") && (
                        <>
                          <Button size="xs" variant="outline" colorScheme="orange"
                                  onClick={() => resolveConflicts("rds")}
                                  data-testid="two-way-conflicts-rds">
                            Conflicts shown: keep RDS
                          </Button>
                          <Button size="xs" variant="outline" colorScheme="orange"
                                  onClick={() => resolveConflicts("supabase")}
                                  data-testid="two-way-conflicts-supabase">
                            Conflicts shown: keep Supabase
                          </Button>
                        </>
                      )}
                    </HStack>

                    {view === "simple" ? (
                      <SimpleView key={`${filter}|${search}`} items={visible} decisions={decisions} expanded={expanded}
                                  onExpand={(key) => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                                  onToggle={(key, on) => setDecision(key, { selected: on })}
                                  onResolve={resolve}
                                  onToggleGroup={(rows, on) => selectVisible(on, rows)} />
                    ) : (
                    <>
                    <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th px={2} />
                            <Th>Table</Th><Th>Row</Th><Th>Action</Th><Th>Why / fields</Th>
                            <Th>Last updated</Th><Th>Decision</Th><Th px={2} />
                          </Tr>
                        </Thead>
                        <Tbody>
                          {visible.slice(0, limit).map((it) => {
                            const key = itemKey(it);
                            return (
                              <ItemRow key={key} item={it} decision={decisions[key] || { selected: false, resolution: "" }}
                                       expanded={!!expanded[key]}
                                       onExpand={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                                       onToggle={(on) => setDecision(key, { selected: on })}
                                       onResolve={(r) => resolve(it, r)} />
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                    {visible.length > limit && (
                      <Button size="xs" variant="ghost" onClick={() => setLimit((l) => l + PAGE)}>
                        Show {Math.min(PAGE, visible.length - limit)} more of {visible.length - limit}
                      </Button>
                    )}
                    </>
                    )}
                    {unresolved > 0 && (
                      <Text fontSize="xs" color="orange.700" data-testid="two-way-unresolved">
                        {unresolved} conflict(s) have no side chosen and will be left as they are.
                      </Text>
                    )}
                  </>
                )}
              </>
            )}
          </VStack>
      </Box>
      <HStack justify="flex-end" px={4} py={3} borderTopWidth="1px">
          {result || error ? (
            <HStack>
              <Button size="sm" onClick={() => compare(mode)} isLoading={loading} data-testid="two-way-compare-again">
                Compare again
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
            </HStack>
          ) : (
            <HStack spacing={3} flexWrap="wrap" justify="flex-end">
              {collabProblem && mode === "buckets" && !result && (
                <Text fontSize="xs" color="orange.700" data-testid="two-way-collab-problem" title={collabProblem}>
                  Open editors will NOT be refreshed and no "Version N Migrated" will be kept: collab server unreachable
                  — {collabProblem}
                </Text>
              )}
              {blockedReason && (
                <Text fontSize="xs" color="orange.700" data-testid="two-way-apply-blocked">{blockedReason}</Text>
              )}
              <Input size="sm" w="200px" placeholder={`type ${phrase} to confirm`} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} isDisabled={!preview || loading}
                     data-testid="two-way-confirm" />
              {reviewing && !applying && (
                <Button size="sm" variant="outline" onClick={() => setReviewing(false)} data-testid="two-way-review-back">
                  Back to list
                </Button>
              )}
              {applying ? (
                <Button size="sm" variant="outline" colorScheme="red" onClick={stop}
                        isDisabled={!!progress?.stopping} data-testid="two-way-stop">
                  {progress?.stopping ? "Stopping…" : "Stop after this batch"}
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
              )}
              {needsReview ? (
                <Button size="sm" colorScheme="blue" onClick={startReview}
                        isDisabled={!preview || loading}
                        data-testid="two-way-review">
                  Review {jsonSelected.length} file change{jsonSelected.length === 1 ? "" : "s"}
                </Button>
              ) : (
                <Button size="sm" colorScheme="green" onClick={apply} isLoading={applying} loadingText="Syncing"
                        isDisabled={!preview || loading || !selected.length || confirm !== phrase || mergePending > 0}
                        title={mergePending > 0 ? `${mergePending} merged note(s) not reviewed yet` : undefined}
                        data-testid="two-way-apply">
                  Apply {selected.length} change{selected.length === 1 ? "" : "s"}
                </Button>
              )}
            </HStack>
          )}
      </HStack>
    </Box>
  );
}
